import { createPath } from 'history';
import { useNavigate, generatePath } from 'react-router-dom';
import { useInvariableMemo } from '../invariable-hooks';
import { QueryParamsHandler } from './query-params-handler';
import { UrlParamsHandler } from './url-params-handler';
import { useBaseName } from './use-basename';

type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (k: infer I) => void
    ? I
    : never;
type Values<T> = T[keyof T];

export interface ParametrizedHistory<
    Keys extends string,
    UrlMappings = void,
    QueryMappings = void,
> {
    path: Overloading<Keys, UrlMappings, QueryMappings, string>;
    href: Overloading<Keys, UrlMappings, QueryMappings, string>;
    push: Overloading<Keys, UrlMappings, QueryMappings, void>;
    replace: Overloading<Keys, UrlMappings, QueryMappings, void>;
    navigate: ReturnType<typeof useNavigate>;
    map: Record<Keys, string>;
}

type AllOverloads<Keys extends string, UrlMappings, QueryMappings, O> = {
    [Prop in Keys]: Prop extends keyof UrlMappings
        ? Prop extends keyof QueryMappings
            ? Partial<UrlMappings[Prop]> extends UrlMappings[Prop]
                ? (key: Prop, urlParams?: UrlMappings[Prop], queryParams?: QueryMappings[Prop]) => O
                : (key: Prop, urlParams: UrlMappings[Prop], queryParams?: QueryMappings[Prop]) => O
            : Partial<UrlMappings[Prop]> extends UrlMappings[Prop]
              ? (key: Prop, urlParams?: UrlMappings[Prop]) => O
              : (key: Prop, urlParams: UrlMappings[Prop]) => O
        : Prop extends keyof QueryMappings
          ? (key: Prop, urlParams?: undefined, queryParams?: QueryMappings[Prop]) => O
          : (key: Prop) => O;
};

type Overloading<Keys extends string, UrlMappings, QueryMappings, O> = UnionToIntersection<
    Values<AllOverloads<Keys, UrlMappings, QueryMappings, O>>
>;

type QueryParamHandlers<QueryMappings> = {
    [Prop in keyof QueryMappings]?: QueryParamsHandler<Record<string, any>>;
};

type UrlParamHandlers<QueryMappings> = {
    [Prop in keyof QueryMappings]?: UrlParamsHandler<Record<string, any>>;
};

interface UseHistoryOpts<PU = void, PQ = void> {
    urlHandlers?: UrlParamHandlers<PU>;
    queryHandlers?: QueryParamHandlers<PQ>;
    nestedPath?: string;
}

/**
 * Create parameterized history to check passed arguments
 *
 * @example
 * type Keys = 'lorem' | 'ipsum' | 'dolor';
 *
 * // interface for routes that has url params like '/lorem/:year(\\d+)' (optional)
 * interface UrlParams {
 *   lorem: { year: number };
 *   ipsum: { name: 'Search' | 'Display' };
 * }
 *
 * // interface for routes that has query params like '/dolor?to=xxx' (optional)
 * interface QueryParams {
 *   dolor: { to: string };
 * }
 *
 * // map keys to react router routes
 * const UrlMap: Record<Keys, string> = {
 *   dolor: '/',
 *   lorem: '/lorem/:year(\\d+)',
 *   ipsum: '/ipsum/:name(Search|Display)',
 * };
 *
 * export const useCoolHistory = () => {
 *     return useParamsHistory<Keys, UrlParams, QueryParams>(UrlMap);
 * }
 *
 * // component
 * export const MyCoolComponent = () => {
 *     const history = useCoolHistory();
 *     const onClick = history.push('lorem', { year: 2021 });
 *
 *     return <Button onClick={onClick}>cool</Button>
 * }
 */
export const useParamsHistory = <Keys extends string, PU = void, PQ = void>(
    paths: Record<Keys, string>,
    opts?: UseHistoryOpts<PU, PQ>
): ParametrizedHistory<Keys, PU, PQ> => {
    const navigate = useNavigate();
    const basename = useBaseName();

    return useInvariableMemo(() => {
        const generateLocation = (path: Keys, urlParams?: any, queryParams?: any) => {
            let searchParams = undefined;

            if (queryParams) {
                const hand = (opts?.queryHandlers as any)?.[path as any] as QueryParamsHandler<any>;

                if (hand) {
                    searchParams = '?' + hand.toString(queryParams);
                } else {
                    searchParams = '?' + new URLSearchParams(queryParams).toString();
                }
            }

            const urlHandler = (opts?.urlHandlers as any)?.[path] as UrlParamsHandler<any>;

            return {
                pathname: generatePath(
                    (opts?.nestedPath ?? '') + paths[path],
                    urlHandler ? urlHandler.toString(urlParams) : urlParams
                ),
                search: searchParams,
            };
        };

        return {
            navigate,
            map: paths,
            path: ((key: Keys, urlParams?: unknown, queryParams?: unknown) => {
                return createPath(generateLocation(key, urlParams, queryParams));
            }) as Overloading<Keys, PU, PQ, string>,
            href: ((key: Keys, urlParams?: unknown, queryParams?: unknown) => {
                const path = createPath(generateLocation(key, urlParams, queryParams));
                if (basename[basename.length - 1] === '/' && path[0] === '/') {
                    return basename + path.slice(1);
                }
                return basename + path;
            }) as Overloading<Keys, PU, PQ, string>,
            push: ((key: Keys, urlParams?: unknown, queryParams?: unknown) => {
                navigate(createPath(generateLocation(key, urlParams, queryParams)));
            }) as Overloading<Keys, PU, PQ, void>,
            replace: ((key: Keys, urlParams?: Partial<PU>, queryParams?: unknown) => {
                navigate(createPath(generateLocation(key, urlParams, queryParams)), {
                    replace: true,
                });
            }) as Overloading<Keys, PU, PQ, void>,
        };
    });
};
